import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Paper,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Home as HomeIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { publicPaymentAPI } from '../services/tripayService';
import { PaymentStatusResponse, TripayPayment } from '../types/tripay';

const PaymentSuccessPage: React.FC = () => {
  const { reference } = useParams<{ reference: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [payment, setPayment] = useState<TripayPayment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (reference) {
      checkPaymentStatus();
      
      // Auto-refresh every 30 seconds for pending payments
      const interval = setInterval(() => {
        if (payment?.status === 'UNPAID') {
          checkPaymentStatus();
        }
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [reference, payment?.status]);

  const checkPaymentStatus = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await publicPaymentAPI.getPaymentDetail(reference!);
      
      if (response.success) {
        setPayment(response.data);
      } else {
        setError(response.message || 'Payment not found');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to check payment status');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PAID':
        return <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />;
      case 'UNPAID':
        return <ScheduleIcon sx={{ fontSize: 64, color: 'warning.main' }} />;
      case 'EXPIRED':
      case 'FAILED':
        return <ErrorIcon sx={{ fontSize: 64, color: 'error.main' }} />;
      default:
        return <ScheduleIcon sx={{ fontSize: 64, color: 'grey.500' }} />;
    }
  };

  const getStatusMessage = (status: string) => {
    switch (status) {
      case 'PAID':
        return {
          title: 'Payment Successful!',
          message: 'Your payment has been processed successfully. Thank you!',
          color: 'success'
        };
      case 'UNPAID':
        return {
          title: 'Payment Pending',
          message: 'Your payment is being processed. Please wait or complete your payment.',
          color: 'warning'
        };
      case 'EXPIRED':
        return {
          title: 'Payment Expired',
          message: 'Your payment session has expired. Please create a new payment.',
          color: 'error'
        };
      case 'FAILED':
        return {
          title: 'Payment Failed',
          message: 'Your payment could not be processed. Please try again.',
          color: 'error'
        };
      default:
        return {
          title: 'Payment Status Unknown',
          message: 'Unable to determine payment status.',
          color: 'grey'
        };
    }
  };

  if (loading && !payment) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" flexDirection="column" alignItems="center" minHeight={400} justifyContent="center">
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Checking payment status...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error && !payment) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Card>
          <CardContent>
            <Stack spacing={3} alignItems="center">
              <ErrorIcon sx={{ fontSize: 64, color: 'error.main' }} />
              <Typography variant="h5" textAlign="center">
                Error Loading Payment
              </Typography>
              <Alert severity="error">
                {error}
              </Alert>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={checkPaymentStatus}
                >
                  Try Again
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<HomeIcon />}
                  onClick={() => navigate('/')}
                >
                  Go Home
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (!payment) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Card>
          <CardContent>
            <Stack spacing={3} alignItems="center">
              <ErrorIcon sx={{ fontSize: 64, color: 'error.main' }} />
              <Typography variant="h5" textAlign="center">
                Payment Not Found
              </Typography>
              <Typography variant="body1" textAlign="center" color="textSecondary">
                The payment reference could not be found.
              </Typography>
              <Button
                variant="contained"
                startIcon={<HomeIcon />}
                onClick={() => navigate('/')}
              >
                Go Home
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    );
  }

  const statusInfo = getStatusMessage(payment.status);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Card>
        <CardContent>
          <Stack spacing={4} alignItems="center">
            {/* Status Icon and Message */}
            <Box textAlign="center">
              {getStatusIcon(payment.status)}
              <Typography variant="h4" sx={{ mt: 2, mb: 1 }}>
                {statusInfo.title}
              </Typography>
              <Typography variant="body1" color="textSecondary">
                {statusInfo.message}
              </Typography>
            </Box>

            {/* Payment Details */}
            <Paper sx={{ p: 3, width: '100%', maxWidth: 500 }}>
              <Typography variant="h6" gutterBottom textAlign="center">
                Payment Details
              </Typography>
              
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="textSecondary">Status:</Typography>
                  <Chip 
                    label={payment.status} 
                    color={statusInfo.color as any}
                    size="small"
                  />
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="textSecondary">Reference:</Typography>
                  <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
                    {payment.reference}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="textSecondary">Payment Method:</Typography>
                  <Typography variant="body2">{payment.payment_method_name}</Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="textSecondary">Amount:</Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary.main">
                    {formatCurrency(payment.amount)}
                  </Typography>
                </Box>

                {payment.bill && (
                  <>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="textSecondary">Bill Number:</Typography>
                      <Typography variant="body2">{payment.bill.bill_number}</Typography>
                    </Box>
                    
                    {payment.bill.package && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">Package:</Typography>
                        <Typography variant="body2">{payment.bill.package.name}</Typography>
                      </Box>
                    )}
                  </>
                )}

                {payment.paid_time && (
                  <>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="textSecondary">Paid At:</Typography>
                      <Typography variant="body2" color="success.main" fontWeight="bold">
                        {new Date(payment.paid_time).toLocaleString('id-ID')}
                      </Typography>
                    </Box>
                  </>
                )}

                {payment.status === 'UNPAID' && (
                  <>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="textSecondary">Expires At:</Typography>
                      <Typography variant="body2" color="error.main" fontWeight="bold">
                        {new Date(payment.expired_time).toLocaleString('id-ID')}
                      </Typography>
                    </Box>
                  </>
                )}
              </Stack>
            </Paper>

            {/* Action Buttons */}
            <Stack direction="row" spacing={2} flexWrap="wrap" justifyContent="center">
              {payment.status === 'UNPAID' && (
                <>
                  <Button
                    variant="contained"
                    onClick={() => window.open(payment.checkout_url, '_blank')}
                  >
                    Complete Payment
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={checkPaymentStatus}
                    disabled={loading}
                  >
                    Refresh Status
                  </Button>
                </>
              )}
              
              {(payment.status === 'PAID' || payment.status === 'EXPIRED' || payment.status === 'FAILED') && (
                <Button
                  variant="contained"
                  startIcon={<HomeIcon />}
                  onClick={() => navigate('/')}
                >
                  Go Home
                </Button>
              )}
            </Stack>

            {/* Auto-refresh notice for pending payments */}
            {payment.status === 'UNPAID' && (
              <Typography variant="caption" color="textSecondary" textAlign="center">
                This page will automatically refresh every 30 seconds to check for payment updates.
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
};

export default PaymentSuccessPage;
